// ============================================================
// RmkBoard — 루미큐브 Konva 보드 Layer  (SEQ-10214 S5c)
//   TD §3.2 BoardLayer 1:1: 보드 전체 — 가운데 영역, 가로 80% × 세로 60%
//   보드 위 RMK_BOARD_SET 세트 표시 + 타일 드래그앤드롭
//   TtrBoard.tsx Konva 패턴 정합 (Stage-as-any, layer/group 구조)
// ============================================================

import React, { useCallback } from 'react'
import {
  Layer,
  Group,
  Rect,
  Text,
} from 'react-konva'
import type Konva from 'konva'
import { RmkBoardSet, RmkTileDetail } from '../store/rummikub'

// ── 타일 색 hex (TD enums.TileColor 가이드값 정합) ────────────
const TILE_COLOR_HEX: Record<string, string> = {
  BLACK:  '#222222',
  RED:    '#D32F2F',
  BLUE:   '#1976D2',
  ORANGE: '#F57C00',
  JOKER:  '#7B1FA2',  // 보라 — 조커 구분
}

// ── 타일 크기 상수 ─────────────────────────────────────────
const TILE_W  = 48
const TILE_H  = 56
const TILE_GAP = 4
const SET_GAP  = 16
const SET_PER_ROW = 4
const BOARD_PADDING = 20

// ── Props ────────────────────────────────────────────────
export interface RmkBoardProps {
  stageWidth:    number
  stageHeight:   number
  boardSets:     RmkBoardSet[]
  boardTiles:    Map<number, RmkTileDetail>
  isMyTurn:      boolean
  myUserCode:    number | null
  // 드래그 출발: 보드 위 타일 → 다른 세트로 이동 (MoveTile)
  onMoveTile?: (tileId: number, fromSetId: number, toSetId: number, position: number) => void
  // 손패 타일 드롭 수신 (PlaceTile) — 드롭 타겟 setId/position 감지
  onDropFromHand?: (tileId: number, targetSetId: number, position: number) => void
}

const RmkBoard: React.FC<RmkBoardProps> = ({
  stageWidth,
  stageHeight,
  boardSets,
  boardTiles,
  isMyTurn,
  onMoveTile,
  onDropFromHand,
}) => {
  // 보드 영역: 가로 80% × 세로 60%, 중앙 배치 (TD §3.2)
  const boardAreaW = stageWidth  * 0.80
  const boardAreaH = stageHeight * 0.60
  const boardOriginX = (stageWidth - boardAreaW) / 2
  const boardOriginY = stageHeight * 0.05   // 상단 5% 여유

  // 세트 배치 계산 (좌→우, 위→아래 그리드)
  function calcSetPosition(setIdx: number): { x: number; y: number } {
    const col = setIdx % SET_PER_ROW
    const row = Math.floor(setIdx / SET_PER_ROW)
    const setBlockW = (TILE_W + TILE_GAP) * 13 + SET_GAP
    const setBlockH = TILE_H + SET_GAP
    return {
      x: boardOriginX + BOARD_PADDING + col * setBlockW,
      y: boardOriginY + BOARD_PADDING + row * setBlockH,
    }
  }

  const handleTileDragEnd = useCallback(
    (
      e: Konva.KonvaEventObject<DragEvent>,
      tileId: number,
      fromSetId: number,
    ) => {
      if (!isMyTurn || !onMoveTile) {
        // 드래그 취소 — 원위치
        e.target.position({ x: 0, y: 0 })
        return
      }
      const stage = e.target.getStage()
      if (!stage) return
      const pos = stage.getPointerPosition()
      if (!pos) return

      // 드롭 위치에서 대상 세트 감지 (단순 히트 테스트)
      let toSetId = 0
      let toPosition = 0
      boardSets.forEach((s, idx) => {
        const { x, y } = calcSetPosition(idx)
        const setW = (TILE_W + TILE_GAP) * Math.max(s.tileIds.length, 1)
        const setH = TILE_H
        if (
          pos.x >= x && pos.x <= x + setW &&
          pos.y >= y && pos.y <= y + setH
        ) {
          toSetId = s.setId
          toPosition = Math.floor((pos.x - x) / (TILE_W + TILE_GAP))
        }
      })

      if (toSetId > 0 && toSetId !== fromSetId) {
        onMoveTile(tileId, fromSetId, toSetId, toPosition)
      }
      // 원위치 (서버 응답 BoardSnapshot으로 확정)
      e.target.position({ x: 0, y: 0 })
    },
    [isMyTurn, onMoveTile, boardSets],
  )

  const handleBoardDropZone = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>, targetSetId: number, position: number) => {
      if (!isMyTurn || !onDropFromHand) return
      // drag data는 PlayRummikub 레벨에서 관리 (dragTileId ref)
      onDropFromHand(0, targetSetId, position)
      void e
    },
    [isMyTurn, onDropFromHand],
  )

  return (
    <Layer>
      {/* 보드 배경 영역 */}
      <Rect
        x={boardOriginX}
        y={boardOriginY}
        width={boardAreaW}
        height={boardAreaH}
        fill="#1a3a1a"
        cornerRadius={8}
        stroke="#4CAF50"
        strokeWidth={2}
      />

      {/* 빈 보드 안내 */}
      {boardSets.length === 0 && (
        <Text
          x={boardOriginX + boardAreaW / 2 - 120}
          y={boardOriginY + boardAreaH / 2 - 12}
          text="보드에 아직 세트가 없습니다"
          fontSize={16}
          fill="#aaa"
          width={240}
          align="center"
        />
      )}

      {/* 세트 렌더링 */}
      {boardSets.map((set, setIdx) => {
        const { x: setX, y: setY } = calcSetPosition(setIdx)
        return (
          <Group key={set.setId} x={setX} y={setY}>
            {/* 세트 라벨 */}
            <Text
              x={0}
              y={-16}
              text={`${set.setType} #${set.setId}`}
              fontSize={10}
              fill="#ccc"
            />
            {/* 세트 배경 */}
            <Rect
              x={0}
              y={0}
              width={(TILE_W + TILE_GAP) * Math.max(set.tileIds.length, 3)}
              height={TILE_H}
              fill="#0d260d"
              cornerRadius={4}
              stroke="#2d6a2d"
              strokeWidth={1}
              // 손패에서 드롭 수신 영역
              onMouseUp={(e: Konva.KonvaEventObject<MouseEvent>) => handleBoardDropZone(e, set.setId, 0)}
            />
            {/* 타일 렌더링 */}
            {set.tileIds.map((tileId, posIdx) => {
              const tile = boardTiles.get(tileId)
              const tileX = posIdx * (TILE_W + TILE_GAP)
              const bgColor = tile
                ? (TILE_COLOR_HEX[tile.color] ?? '#555')
                : '#444'
              const label = tile
                ? (tile.isJoker ? '★' : String(tile.number))
                : '?'
              const textColor = tile?.color === 'ORANGE' ? '#000' : '#fff'

              return (
                <Group
                  key={tileId}
                  x={tileX}
                  y={0}
                  draggable={isMyTurn}
                  onDragEnd={(e: Konva.KonvaEventObject<DragEvent>) =>
                    handleTileDragEnd(e, tileId, set.setId)
                  }
                >
                  <Rect
                    x={0}
                    y={0}
                    width={TILE_W}
                    height={TILE_H}
                    fill={bgColor}
                    cornerRadius={4}
                    stroke="#fff"
                    strokeWidth={1}
                    shadowBlur={3}
                    shadowColor="rgba(0,0,0,0.4)"
                  />
                  <Text
                    x={0}
                    y={TILE_H / 2 - 10}
                    width={TILE_W}
                    text={label}
                    fontSize={18}
                    fontStyle="bold"
                    fill={textColor}
                    align="center"
                  />
                </Group>
              )
            })}
          </Group>
        )
      })}
    </Layer>
  )
}

export default RmkBoard
